import { LOCALE_CODE } from '@pkg/config/languages/localeCodes';
import { surpriseBagSeoKeyword } from '@pkg/config/sanity/slugs';

export enum PAGE_TYPE {
  UNKNOWN_PAGE = 'UNKNOWN-PAGE',
  HOMEPAGE = 'HOMEPAGE',
  ARTICLE_SUFFIX = '-ARTICLE',
  HOME_SUFFIX = '-HOME',
  SURPRISE_BAG_FIND = 'FIND_ITEMS',
  SURPRISE_BAG = 'SCREEN_ITEM'
}

export function getLocale(path: string): string {
  const locales = Object.values(LOCALE_CODE);
  const urlLower = path.toLowerCase();
  const matchedLocale = locales.find((locale) => {
    const regex = new RegExp(`${locale}(?:/|$)`); // Match locale followed by '/' or end of string
    return regex.test(urlLower);
  });
  return matchedLocale || LOCALE_CODE.EN_GB;
}

export function getPageType(path: string): string {
  const locale = getLocale(path);
  try {
    const url = new URL(path);
    const pathname = url.pathname.includes(locale) ? url.pathname : `/${locale}${url.pathname}`;

    const urlArr = pathname.split('/').filter(Boolean);

    const lastSegment = urlArr[urlArr.length - 1];
    const isSurpriseBagItem = urlArr[1] === surpriseBagSeoKeyword &&
      /^(\d+|[\w-]+-\d+)$/.test(lastSegment);

    if (urlArr[1] === surpriseBagSeoKeyword && urlArr.length === 2) {
      return PAGE_TYPE.SURPRISE_BAG_FIND;
    } else if (urlArr[1] === surpriseBagSeoKeyword && isSurpriseBagItem && urlArr.length > 2) {
      return PAGE_TYPE.SURPRISE_BAG;
    } else if (urlArr.length === 1) {
      return PAGE_TYPE.HOMEPAGE;
    } else if (urlArr.length === 2) {
      return `${urlArr[1].toUpperCase() || PAGE_TYPE.UNKNOWN_PAGE}${PAGE_TYPE.HOME_SUFFIX}`;
    } else if (urlArr.length > 2) {
      return `${urlArr[1].toUpperCase()}${PAGE_TYPE.ARTICLE_SUFFIX}`;
    }

    return PAGE_TYPE.UNKNOWN_PAGE;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log('Analytics Error: unable to identify page ', e);
    return PAGE_TYPE.UNKNOWN_PAGE;
  }
}

const ONE_LINK_HOST = 'tgtg.onelink.me';

/**
 * Collapses dynamic OneLink paths and query strings so Element Clicked hrefs group in Amplitude.
 * e.g. https://tgtg.onelink.me/OGjG/718n7df7?af_sub1=123 → https://tgtg.onelink.me/OGjG
 */
export function normalizeElementHrefForAmplitude(href: string): string {
  const trimmed = href.trim();
  if (!trimmed) {
    return trimmed;
  }

  try {
    const url = new URL(trimmed);

    if (url.hostname === ONE_LINK_HOST) {
      const [oneLinkId] = url.pathname.split('/').filter(Boolean);
      return oneLinkId ? `${url.origin}/${oneLinkId}` : url.origin;
    }

    url.search = '';
    url.hash = '';
    return `${url.origin}${url.pathname}`.replace(/\/$/, '') || url.origin;
  } catch {
    return trimmed.split(/[?#]/)[0] ?? trimmed;
  }
}
